/**
 * FikoFeatureTani — Otomatik İşleme Özelliği Tanıma (Feature Recognition)
 * B-Rep yüz gruplarından (occt_faceGroups) delik/cep/düzlem/pah tanır → CAM operasyonu önerir.
 * Mesh-bağımsız: govdeMesh.occt_faceGroups (surfaceType/center/normal/axis/radius) + per-face Z/alan kullanır.
 */
import * as THREE from 'three';

export type OzellikTip = 'delik' | 'cep' | 'duzlemUst' | 'pah' | 'silindirDis';

export interface ITanananOzellik {
  tip: OzellikTip;
  etiket: string;
  faceId: number;
  konum: { x: number; y: number; z: number };   // dünya koordinatı
  cap?: number;        // delik/silindir çapı (mm)
  derinlik?: number;   // delik/cep derinliği (mm)
  alan?: number;       // düzlem/cep alanı (cm²)
  camOp?: 'delik' | 'cep' | 'yuzey';   // önerilen CAM operasyonu (yoksa bilgi amaçlı)
}

const _v0 = new THREE.Vector3(), _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3();
const _e1 = new THREE.Vector3(), _e2 = new THREE.Vector3(), _cr = new THREE.Vector3(), _w = new THREE.Vector3();

/** Mesh'in B-Rep yüzlerini tarayıp tanınan işleme özelliklerini döndürür. */
export function ozellikleriTani(mesh: any): ITanananOzellik[] {
  if (!mesh?.geometry) return [];
  const fg: any[] = mesh.occt_faceGroups || [];
  const geo = mesh.geometry;
  const pos = geo.attributes?.['position'];
  const grupAttr = geo.attributes?.['grup'];
  if (!fg.length || !pos || !grupAttr) return [];
  mesh.updateMatrixWorld?.(true);
  const mw = mesh.matrixWorld;

  // Per-faceId: local Z aralığı + dünya alanı + dünya centroid (alan-ağırlıklı)
  const zr = new Map<number, { min: number; max: number }>();
  const fAlan = new Map<number, number>();   // mm²
  const fCx = new Map<number, number>(), fCy = new Map<number, number>(), fCz = new Map<number, number>();
  for (let v = 0; v < pos.count; v++) { const fid = grupAttr.getZ(v); const z = pos.getZ(v); const e = zr.get(fid); if (!e) zr.set(fid, { min: z, max: z }); else { if (z < e.min) e.min = z; if (z > e.max) e.max = z; } }
  const idx = geo.index; const triN = idx ? idx.count : pos.count;
  for (let i = 0; i < triN; i += 3) {
    const a = idx ? idx.getX(i) : i, b = idx ? idx.getX(i + 1) : i + 1, c2 = idx ? idx.getX(i + 2) : i + 2;
    const fid = grupAttr.getZ(a);
    _v0.fromBufferAttribute(pos, a).applyMatrix4(mw); _v1.fromBufferAttribute(pos, b).applyMatrix4(mw); _v2.fromBufferAttribute(pos, c2).applyMatrix4(mw);
    _e1.subVectors(_v1, _v0); _e2.subVectors(_v2, _v0); _cr.crossVectors(_e1, _e2);
    const ar = _cr.length() / 2; if (ar <= 0) continue;
    fAlan.set(fid, (fAlan.get(fid) || 0) + ar);
    const cx = (_v0.x + _v1.x + _v2.x) / 3, cy = (_v0.y + _v1.y + _v2.y) / 3, cz = (_v0.z + _v1.z + _v2.z) / 3;
    fCx.set(fid, (fCx.get(fid) || 0) + cx * ar); fCy.set(fid, (fCy.get(fid) || 0) + cy * ar); fCz.set(fid, (fCz.get(fid) || 0) + cz * ar);
  }

  const bbox = new THREE.Box3().setFromObject(mesh);
  const ust = bbox.max.z, alt = bbox.min.z, yukseklik = Math.max(1, ust - alt);

  const out: ITanananOzellik[] = [];
  for (let i = 0; i < fg.length; i++) {
    const g = fg[i]; if (!g) continue;
    const tip = String(g.surfaceType || '').toLowerCase();
    const sd = g.surfaceData || g;
    const ar = fAlan.get(i) || 0; if (ar < 0.01) continue;
    const cw = { x: (fCx.get(i) || 0) / ar, y: (fCy.get(i) || 0) / ar, z: (fCz.get(i) || 0) / ar };

    if (tip === 'cylinder' && sd.radius > 0 && sd.axis) {
      const ax = _v0.set(sd.axis.x, sd.axis.y, sd.axis.z).normalize();
      const dikey = Math.abs(ax.z) > 0.8;
      const r = sd.radius;
      const z = zr.get(i); const derinlik = z ? z.max - z.min : yukseklik;
      // dünya konumu: yüzün üst ağzı (delik için ~zmax)
      _w.set(sd.center ? sd.center.x : cw.x, sd.center ? sd.center.y : cw.y, z ? z.max : cw.z).applyMatrix4(mw);
      if (dikey && r <= 50) {
        out.push({ tip: 'delik', etiket: `Delik Ø${(r * 2).toFixed(1)} × ${derinlik.toFixed(0)}mm`, faceId: i, konum: { x: _w.x, y: _w.y, z: _w.z }, cap: r * 2, derinlik, camOp: 'delik' });
      } else {
        out.push({ tip: 'silindirDis', etiket: `Silindir Ø${(r * 2).toFixed(1)}mm`, faceId: i, konum: { x: cw.x, y: cw.y, z: cw.z }, cap: r * 2 });
      }
    } else if (tip === 'cone') {
      out.push({ tip: 'pah', etiket: 'Pah / Konik yüzey', faceId: i, konum: cw });
    } else if (tip === 'plane' && g.normal) {
      const nz = g.normal.z;
      if (nz > 0.8) {   // yukarı bakan düz yüz
        const alanCm2 = ar / 100;
        const tepedeMi = cw.z >= ust - yukseklik * 0.02;
        if (tepedeMi) out.push({ tip: 'duzlemUst', etiket: `Üst düzlem ${alanCm2.toFixed(1)}cm²`, faceId: i, konum: cw, alan: alanCm2, camOp: 'yuzey' });
        else if (cw.z > alt + yukseklik * 0.02) out.push({ tip: 'cep', etiket: `Cep tabanı ${alanCm2.toFixed(1)}cm² @${(cw.z - alt).toFixed(0)}mm`, faceId: i, konum: cw, alan: alanCm2, derinlik: ust - cw.z, camOp: 'cep' });
      }
    }
  }
  // delikleri çapa göre grupla-sırala, en çok iş çıkaranı üste
  out.sort((a, b) => (a.tip === b.tip ? 0 : a.tip === 'delik' ? -1 : 1));
  return out;
}

/** Tanınan özellikleri tipe göre özetler (panel başlığı için). */
export function ozellikOzet(liste: ITanananOzellik[]): Record<OzellikTip, number> {
  const o: any = { delik: 0, cep: 0, duzlemUst: 0, pah: 0, silindirDis: 0 };
  for (const x of liste) o[x.tip]++;
  return o;
}
